import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Button,
  Flex,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack,
  useToast,
  Select,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  Divider,
  Avatar,
} from "@chakra-ui/react";
import {
  FiChevronLeft,
  FiDownload,
  FiFilter,
  FiMoreVertical,
  FiUserPlus,
  FiCalendar,
  FiMail,
  FiPhone,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import { useParams, useNavigate } from "react-router-dom";
import { applicationService, atsUtils } from "../atsService";
import { jobRequisitionService } from "../jobRequisitionService";

const ApplicationReview = () => {
  const { requisitionId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [requisition, setRequisition] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedStage, setSelectedStage] = useState("all");
  const [offerReadyFilter, setOfferReadyFilter] = useState("all");

  useEffect(() => {
    fetchRequisitionDetails();
    fetchApplications();
  }, [requisitionId]);

  const fetchRequisitionDetails = async () => {
    try {
      console.log("Fetching requisition details for ID:", requisitionId);
      const response = await jobRequisitionService.getRequisitionById(
        requisitionId
      );
      console.log("Requisition response:", response);
      console.log("Setting requisition to:", response);
      setRequisition(response);
    } catch (error) {
      console.log("Error fetching requisition:", error);
      toast({
        title: "Error",
        description: "Failed to fetch requisition details",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationService.getApplications(requisitionId);
      setApplications(response.data.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch applications",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (applicationId, newStage) => {
    try {
      await applicationService.updateApplicationStage(applicationId, {
        newStage,
      });
      await fetchApplications(); // Refresh the applications list
      toast({
        title: "Success",
        description: "Application stage updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update application stage",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleAddCandidate = () => {
    // Navigate to create application page or open modal
    navigate(`/recruiting/applications/create?requisitionId=${requisitionId}`);
  };

  const handleExport = () => {
    // Export applications data as CSV
    const csvContent = [
      ["Name", "Email", "Stage", "Applied Date", "Status"],
      ...applications.map((app) => [
        `${app.candidateId?.firstName} ${app.candidateId?.lastName}`,
        app.candidateId?.email,
        atsUtils.formatApplicationStatus(app.currentStage),
        new Date(app.appliedAt).toLocaleDateString(),
        app.status,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${requisition?.jobTitle || "applications"}_export.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Success",
      description: "Applications exported successfully",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };
  const handleViewDetails = (applicationId) => {
    navigate(`/recruiting/applications/${applicationId}`);
  };

  const handleSendEmail = async (applicationId) => {
    try {
      await applicationService.sendEmail(applicationId, {
        subject: "Update on your application",
        message: "We wanted to provide an update on your application status.",
        template: "follow_up",
      });
      toast({
        title: "Success",
        description: "Email sent successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send email",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleScheduleInterview = (applicationId) => {
    // Open interview scheduling modal
    toast({
      title: "Feature Coming Soon",
      description: "Interview scheduling will be available soon",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleMoveToNextStage = async (applicationId) => {
    const currentApp = applications.find((app) => app._id === applicationId);
    if (!currentApp) return;

    const stageOrder = [
      "applied",
      "screening",
      "assessment",
      "interview_1",
      "interview_2",
      "offer_pending",
      "hired",
    ];
    const currentIndex = stageOrder.indexOf(currentApp.currentStage);
    const nextStage = stageOrder[currentIndex + 1];

    if (nextStage) {
      await handleStageChange(applicationId, nextStage);
    } else {
      toast({
        title: "Info",
        description: "Application is already in the final stage",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleReject = async (applicationId) => {
    await handleStageChange(applicationId, "rejected");
  };

  const filteredApplications = applications.filter((app) => {
    const stageMatch =
      selectedStage === "all" || app.currentStage === selectedStage;
    const offerReadyMatch =
      offerReadyFilter === "all" ||
      (offerReadyFilter === "ready" &&
        ["final_interview", "offer_pending"].includes(app.currentStage));
    return stageMatch && offerReadyMatch;
  });

  console.log("Rendering with requisition:", requisition);

  return (
    <Box p={6}>
      <Flex mb={6} justify="space-between" align="center">
        <HStack>
          <IconButton
            icon={<FiChevronLeft />}
            onClick={() => navigate("/recruiting/ats")}
            aria-label="Go back"
          />
          <VStack align="start" spacing={1}>
            <Heading size="lg">{requisition?.jobTitle || "Loading..."}</Heading>
            <Text color="gray.600">
              {requisition?.department || "Loading"} •{" "}
              {requisition?.location || "Loading"}
            </Text>
          </VStack>
        </HStack>
        <HStack spacing={4}>
          <Button
            leftIcon={<FiUserPlus />}
            colorScheme="blue"
            onClick={handleAddCandidate}
          >
            Add Candidate
          </Button>
          <Button leftIcon={<FiDownload />} onClick={handleExport}>
            Export
          </Button>
        </HStack>
      </Flex>

      <Grid templateColumns="repeat(4, 1fr)" gap={6} mb={6}>
        <Card>
          <CardBody>
            <VStack align="start">
              <Text color="gray.600">Total Applications</Text>
              <Heading size="lg">{applications.length}</Heading>
            </VStack>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <VStack align="start">
              <Text color="gray.600">In Review</Text>
              <Heading size="lg">
                {
                  applications.filter((app) =>
                    [
                      "screening",
                      "interview_1",
                      "interview_2",
                      "assessment",
                    ].includes(app.currentStage)
                  ).length
                }
              </Heading>
            </VStack>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <VStack align="start">
              <Text color="gray.600">Shortlisted</Text>
              <Heading size="lg">
                {
                  applications.filter((app) =>
                    ["offer_pending", "offer_accepted"].includes(
                      app.currentStage
                    )
                  ).length
                }
              </Heading>
            </VStack>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <VStack align="start">
              <Text color="gray.600">Rejected</Text>
              <Heading size="lg">
                {
                  applications.filter((app) => app.currentStage === "rejected")
                    .length
                }
              </Heading>
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      <Card>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">Applications</Heading>
            <HStack spacing={4}>
              <Select
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
                w="200px"
              >
                <option value="all">All Stages</option>
                <option value="applied">Applied</option>
                <option value="screening">Screening</option>
                <option value="interview_1">First Interview</option>
                <option value="interview_2">Second Interview</option>
                <option value="offer_pending">Offer Pending</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
              </Select>
              <Select
                value={offerReadyFilter}
                onChange={(e) => setOfferReadyFilter(e.target.value)}
                w="180px"
              >
                <option value="all">All Applications</option>
                <option value="ready">Offer Ready Only</option>
              </Select>
              <Button
                leftIcon={<FiFilter />}
                onClick={() => {
                  toast({
                    title: "Advanced Filters",
                    description: "Advanced filtering will be available soon",
                    status: "info",
                    duration: 3000,
                    isClosable: true,
                  });
                }}
              >
                Filter
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Box p={8} textAlign="center">
              <Text>Loading applications...</Text>
            </Box>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Candidate</Th>
                  <Th>Current Stage</Th>
                  <Th>Status</Th>
                  <Th>Applied Date</Th>
                  <Th>Last Update</Th>
                  <Th>Offer Ready</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredApplications.map((app) => (
                  <Tr key={app._id}>
                    <Td>
                      <HStack spacing={3}>
                        <Avatar
                          size="sm"
                          name={`${app.candidateId?.firstName} ${app.candidateId?.lastName}`}
                        />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium">
                            {app.candidateId?.firstName}{" "}
                            {app.candidateId?.lastName}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {app.candidateId?.email}
                          </Text>
                        </VStack>
                      </HStack>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={atsUtils.getStatusColor(app.currentStage)}
                      >
                        {atsUtils.formatApplicationStatus(app.currentStage)}
                      </Badge>
                    </Td>
                    <Td>{app.status}</Td>
                    <Td>{new Date(app.appliedAt).toLocaleDateString()}</Td>
                    <Td>{new Date(app.updatedAt).toLocaleDateString()}</Td>
                    <Td>
                      {["final_interview", "offer_pending"].includes(
                        app.currentStage
                      ) ? (
                        <Badge colorScheme="purple" variant="subtle">
                          <FiFileText
                            style={{ display: "inline", marginRight: "4px" }}
                          />
                          Ready
                        </Badge>
                      ) : (
                        <Text color="gray.500" fontSize="sm">
                          -
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem
                            icon={<FiFileText />}
                            onClick={() => handleViewDetails(app._id)}
                          >
                            View Details
                          </MenuItem>
                          <MenuItem
                            icon={<FiMail />}
                            onClick={() => handleSendEmail(app._id)}
                          >
                            Send Email
                          </MenuItem>
                          <MenuItem
                            icon={<FiCalendar />}
                            onClick={() => handleScheduleInterview(app._id)}
                          >
                            Schedule Interview
                          </MenuItem>
                          <MenuItem
                            icon={<FiCheckCircle />}
                            onClick={() => handleMoveToNextStage(app._id)}
                          >
                            Move to Next Stage
                          </MenuItem>
                          {["final_interview", "offer_pending"].includes(
                            app.currentStage
                          ) && (
                            <MenuItem
                              icon={<FiFileText />}
                              onClick={() => {
                                // Navigate to application details with create offer modal open
                                window.open(
                                  `/recruiting/applications/${app._id}?createOffer=true`,
                                  "_blank"
                                );
                              }}
                            >
                              Create Offer
                            </MenuItem>
                          )}
                          <MenuItem
                            icon={<FiXCircle />}
                            color="red.500"
                            onClick={() => handleReject(app._id)}
                          >
                            Reject
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
          {!loading && filteredApplications.length === 0 && (
            <Box p={8} textAlign="center">
              <Text color="gray.500">No applications found</Text>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default ApplicationReview;
